import { useMemo, useState } from "react";
import { Check, AlertCircle, ArrowRight, MapPin, Loader2, Info } from "lucide-react";
import { STATES } from "@/config/states";
import { INTAKE_URLS, LAUNCHED } from "@/config/intake";
import { joinWaitlist } from "@/lib/waitlist";
import { useEligibility } from "@/hooks/use-eligibility";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PR_NOTE = "Puerto Rico is supported — orders ship from our mainland 503A pharmacies in 5–7 business days.";

type Status =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "success"; duplicate: boolean }
  | { kind: "error"; message: string };

export const StateEligibility = () => {
  const { code, state, setCode } = useEligibility();
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const intakeHref = useMemo(
    () => state ? `${INTAKE_URLS.default}?state=${state.code}` : INTAKE_URLS.default,
    [state]
  );

  const emailValid = EMAIL_RE.test(email.trim());
  const showInlineError = touched && email.length > 0 && !emailValid;
  const isPR = code === "PR";

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!state || !emailValid) return;
    setStatus({ kind: "loading" });
    const res = await joinWaitlist({ email, state: state.code, placement: "hero_state_eligibility" });
    if (res.ok === true) {
      setStatus({ kind: "success", duplicate: res.duplicate });
    } else {
      setStatus({ kind: "error", message: res.error });
    }
  };

  return (
    <div className="mt-8 rounded-2xl border border-border bg-card/80 backdrop-blur p-4 md:p-5">
      <label htmlFor="state-eligibility" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-foreground/70 font-medium mb-3">
        <MapPin className="size-3.5 text-accent" aria-hidden />
        Available in all 50 states + Puerto Rico — check yours
      </label>

      <div className="flex flex-col sm:flex-row gap-2.5">
        <div className="relative flex-1">
          <select
            id="state-eligibility"
            value={code}
            onChange={(e) => {
              setCode(e.target.value, "hero_state_eligibility");
              setStatus({ kind: "idle" });
              setTouched(false);
            }}
            className="w-full appearance-none rounded-full border border-border bg-background px-4 py-3 text-sm text-foreground pr-9 focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
            aria-label="Select your state"
          >
            <option value="" disabled>Select your state</option>
            {STATES.map((s) => (
              <option key={s.code} value={s.code}>{s.name}</option>
            ))}
          </select>
          <span aria-hidden className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-foreground/40 text-xs">▾</span>
        </div>

        {LAUNCHED && state && (
          <a
            href={intakeHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-3 text-sm font-medium hover:opacity-90 transition focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
          >
            Continue
            <ArrowRight className="size-4" />
          </a>
        )}
      </div>

      {isPR && (
        <p className="mt-3 inline-flex items-start gap-1.5 text-xs text-foreground/70 bg-accent/8 rounded-lg px-3 py-2">
          <Info className="size-3.5 text-accent mt-0.5 shrink-0" />
          {PR_NOTE}
        </p>
      )}

      {LAUNCHED && state && (
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-foreground/70">
          <Check className="size-3.5 text-accent" strokeWidth={2.5} />
          You're in. Olive is live in {state.name}.
        </p>
      )}

      {state && !LAUNCHED && status.kind !== "success" && (
        <form onSubmit={onSubmit} className="mt-3.5 space-y-2.5" noValidate>
          <p className="inline-flex items-start gap-1.5 text-xs text-foreground/70">
            <AlertCircle className="size-3.5 text-accent mt-0.5 shrink-0" />
            Olive is launching soon. Drop your email and we'll let {state.name} know the moment we go live.
          </p>
          <div className="flex flex-col sm:flex-row gap-2.5">
            <div className="flex-1">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (status.kind === "error") setStatus({ kind: "idle" });
                }}
                onBlur={() => setTouched(true)}
                placeholder="you@email.com"
                aria-invalid={showInlineError}
                aria-describedby={showInlineError ? "waitlist-email-error" : undefined}
                disabled={status.kind === "loading"}
                className={`w-full rounded-full border bg-background px-4 py-3 text-sm focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 disabled:opacity-60 ${
                  showInlineError ? "border-destructive" : "border-border"
                }`}
              />
            </div>
            <button
              type="submit"
              disabled={status.kind === "loading" || !emailValid}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-3 text-sm font-medium hover:opacity-90 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status.kind === "loading" ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden /> Saving…
                </>
              ) : (
                "Notify me"
              )}
            </button>
          </div>
          {showInlineError && (
            <p id="waitlist-email-error" className="text-xs text-destructive">
              Please enter a valid email address.
            </p>
          )}
          {status.kind === "error" && (
            <p role="alert" className="inline-flex items-start gap-1.5 text-xs text-destructive">
              <AlertCircle className="size-3.5 mt-0.5 shrink-0" />
              {status.message}
            </p>
          )}
        </form>
      )}

      {status.kind === "success" && (
        <p role="status" className="mt-3 inline-flex items-center gap-1.5 text-xs text-foreground/70">
          <Check className="size-3.5 text-accent" strokeWidth={2.5} />
          {status.duplicate
            ? `You're already on the list for ${state?.name} — we'll be in touch.`
            : `You're on the list. We'll email you the moment ${state?.name} opens up.`}
        </p>
      )}
    </div>
  );
};

